import { Box, FormHelperText } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import type { Dayjs } from 'dayjs';
import React from 'react';
import { CONTROL_HEIGHT } from './controlSizing';

export const messageScheduleRowSx = {
  marginTop: '10px',
  marginBottom: '10px',
  columnGap: 1.5,
  rowGap: 1,
  flexWrap: 'nowrap',
  overflowX: 'auto',
  pb: 0.5,
} satisfies SxProps<Theme>;

const messageScheduleDateTimePickerContainerSx = {
  marginTop: '13px',
  marginLeft: '-5px',
  flex: { xs: '0 0 240px', sm: '0 0 290px' },
  width: { xs: '240px', sm: '250px' },
  maxWidth: { xs: '240px', sm: '250px' },
  minWidth: 0,
} satisfies SxProps<Theme>;

export const messageScheduleTimePickerContainerSx = {
  ...messageScheduleDateTimePickerContainerSx,
  flex: { xs: '0 0 190px', sm: '0 0 220px' },
  width: { xs: '190px', sm: '220px' },
  maxWidth: { xs: '190px', sm: '220px' },
} satisfies SxProps<Theme>;

export const messageSchedulePickerDemoContainerSx = {
  marginTop: 0,
  paddingTop: 0,
  overflow: 'visible',
  '& .MuiTextField-root': {
    minWidth: 0,
    width: '100%',
  },
} satisfies SxProps<Theme>;

export const messageSchedulePickerSx = {
  width: '100%',
  overflow: 'hidden',
  '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
  '& .MuiInputLabel-formControl': { top: '-2px' },
} satisfies SxProps<Theme>;


type MessageScheduleDateTimePickerProps = {
  disabled?: boolean;
  hasError?: boolean;
  helperText?: string;
  minDateTime?: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  value: Dayjs | null;
};

export const MessageScheduleDateTimePicker: React.FC<MessageScheduleDateTimePickerProps> = ({
  disabled,
  hasError,
  helperText,
  minDateTime,
  onChange,
  value,
}) => {
  const shouldShowHelperText = helperText !== undefined;

  return (
    <Box sx={messageScheduleDateTimePickerContainerSx}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={['DateTimePicker']} sx={messageSchedulePickerDemoContainerSx}>
          <DateTimePicker
            sx={messageSchedulePickerSx}
            slotProps={{
              textField: {
                ...(shouldShowHelperText ? { helperText: ' ', error: !!hasError } : {}),
                // Only forces the error outline; leaves the picker's own validation in charge otherwise.
                ...(hasError ? { error: true } : {}),
              },
            }}
            ampm
            format="MM/DD/YYYY hh:mm A"
            disablePast
            minDateTime={minDateTime && minDateTime.isValid() ? minDateTime : undefined}
            closeOnSelect={false}
            onChange={onChange}
            disabled={disabled}
            value={value}
          />
        </DemoContainer>
      </LocalizationProvider>
      {shouldShowHelperText && (
        <FormHelperText error={!!hasError} sx={{ marginTop: '-20px', minHeight: '20px' }}>
          {helperText || ' '}
        </FormHelperText>
      )}
    </Box>
  );
};
